import React, { FC, useEffect, useState } from 'react';
import { FormikErrors, useFormik } from 'formik';
import Yup from '~yup';
import { GetCountries } from 'react-country-state-city';
import { Country } from 'react-country-state-city/dist/esm/types';

import {
    TAdditionalUserDetails,
    TCreateNewCustomerAdditionalUsersReduxParams,
    TCreateNewCustomerReduxParams,
    TCreateNewCustomerValidation,
} from '~types/feat/customer';
import { useAppSelector } from '~redux/hooks';

import LabelledInput from '~components/atoms/LabelledInput';
import LabelledDropdown from '~components/atoms/LabelledDropdown';
import ActionButton from '~components/atoms/ActionButton';
import Text from '~components/atoms/Text';
import Checkbox from '~components/atoms/Checkbox';

import {
    BranchStatusEnum,
    CustomerAccFundTypeEnum,
    CustomerAccTypeEnum,
    CustomerIdTypeEnum,
    CustomerRaceEnum,
    FundTypeEnum,
} from '~utils/enums';
import UtilFunctions from '~utils/functions';

import * as FormStyles from './_customerManagementCreateCustomerForm.module.scss';
import countryList from '~utils/country';

type TCustomerManagementCreateCustomerForm = {
    submitButtonLabel: string;
    onSubmit: (data: TCreateNewCustomerReduxParams) => void;
    error: string;
    inputIsDisabled?: boolean;
    actionButtonIsLoading?: boolean;
    actionButtonClassName?: string;
    actionButtonIsDisabled?: boolean;
}

const CustomerManagementCreateCustomerForm: FC<TCustomerManagementCreateCustomerForm> = ({
    submitButtonLabel,
    onSubmit,
    error,
    inputIsDisabled,
    actionButtonIsLoading,
    actionButtonClassName,
    actionButtonIsDisabled,
}) => {
    const { Misc: { blockEnterKey, sanitizeReferralCode } } = UtilFunctions;

    const branchesList = useAppSelector(state => state.branch.allBranches);
    const { allBranchAccess, branchCode } = useAppSelector(state => state.auth.userDetails);

    const [countries, setCountries] = useState<Country[]>([]);

    useEffect(() => {
        if (branchesList.length > 0 && !allBranchAccess) {
            formik.setFieldValue('branchCode', branchCode);
            formik.setFieldValue('branchName', branchesList.find((branch) => branch.branchCode === branchCode)?.branchName);
        }
    }, [branchesList]);

    const CustomerManagementCreateCustomerFormValidation = Yup.object().shape({
        name: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().validateCustomerName().label('Customer Name').required('Customer Name is required'),
        email: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().email().label('Email'),
        CIFNo: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().matchNumbersOnly().max(11).label('CIF Number').required('CIF Number is required'),
        idType: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().oneOf(Object.values(CustomerIdTypeEnum).filter(e => Number.isNaN(Number(e))) as string[]).label('ID Type').required('ID Type is required'),
        idNo: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace()
            .when('idType', {
                is: (val: keyof typeof CustomerIdTypeEnum) => {
                    return val === CustomerIdTypeEnum[CustomerIdTypeEnum.IC];
                },
                then: (schema) => schema.length(12).label('IC number').matchNumbersOnly(),
            })
            .when('idType', {
                is: (val: keyof typeof CustomerIdTypeEnum) => {
                    return val === CustomerIdTypeEnum[CustomerIdTypeEnum['Company Registration No']];
                },
                then: (schema) => schema.max(12).label('Company Registration Number').matches(/^[a-zA-Z0-9-]+$/g, { message: 'Invalid character detected' }),
            })
            .when('idType', {
                is: (val: keyof typeof CustomerIdTypeEnum) => {
                    return val === CustomerIdTypeEnum[CustomerIdTypeEnum.Passport];
                },
                then: (schema) => schema.max(12).label('Passport number').matchNumbersAndAlphabets(),
            })
            .when('idType', {
                is: (val: keyof typeof CustomerIdTypeEnum) => {
                    return val === CustomerIdTypeEnum[CustomerIdTypeEnum.MyTentera];
                },
                then: (schema) => schema
                    .length(7, 'MyTentera Number must be exactly 7 characters')
                    .matches(/^[a-zA-Z0-9]+$/, {
                        message: 'MyTentera Number can be alphanumeric',
                    })
                    .label('MyTentera Number'),
            })
            .required('ID Number is required'),
        contactNo: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().max(17).matchNumbersOnly().label('Contact Number').required('Contact Number is required'),
        branchName: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().oneOf(branchesList.map(branch => branch.branchName), 'Invalid branch name').label('Branch Name').required('Branch Name is required'),
        branchCode: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().oneOf(branchesList.map(branch => branch.branchCode), 'Invalid branch code').label('Branch Code').required('Branch Code is required'),
        nationality: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().oneOf(countries.map(country => country.name), 'Invalid country').label('Nationality').required('Nationality is required'),
        occupation: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().label('Occupation').required('Occupation is required'),
        addressLine1: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().label('Address Line 1').required('Address Line 1 is required'),
        addressLine2: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().label('Address Line 2'),
        city: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().label('City').required('City is required'),
        state: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().label('State').required('State is required'),
        postCode: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().label('Postcode').required('Postcode is required'),
        race: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().oneOf(Object.values(CustomerRaceEnum).filter(e => Number.isNaN(Number(e))) as string[]).label('Race').required('Race is required'),
        fundType: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().oneOf(Object.values(CustomerAccFundTypeEnum).filter(e => Number.isNaN(Number(e))) as string[]).label('Fund Type').required('Fund Type is required'),
        refCode: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().matches(/^[^=+\-@]*$/, { message: 'Cannot include these characters (=, +, -,@)' }).label('Referral Code'),
        accountType: Yup.number().required(),
        additionalUserDetails: Yup.array().of(
            Yup.object().shape({
                name: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().validateCustomerName().typeError('Child name is required')
                    .when([], {
                        is: () => formik.values.additionalUserDetailsToggle,
                        then: (schema) => schema.required('Child Name is required'),
                        otherwise: (schema) => schema.notRequired().nullable(),
                    }),
                idType: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().oneOf(Object.values(CustomerIdTypeEnum).filter(e => Number.isNaN(Number(e))) as string[])
                    .when([], {
                        is: () => formik.values.additionalUserDetailsToggle,
                        then: (schema) => schema.required('Child ID Type is required'),
                        otherwise: (schema) => schema.notRequired().nullable(),
                    }),
                idNo: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace()
                    .when('idType', {
                        is: (val: keyof typeof CustomerIdTypeEnum) => {
                            return val === CustomerIdTypeEnum[CustomerIdTypeEnum.IC];
                        },
                        then: (schema) => schema.length(12).label('IC number').matchNumbersOnly(),
                    })
                    .when('idType', {
                        is: (val: keyof typeof CustomerIdTypeEnum) => {
                            return val === CustomerIdTypeEnum[CustomerIdTypeEnum['Company Registration No']];
                        },
                        then: (schema) => schema.max(12).label('Company Registration Number').matches(/^[a-zA-Z0-9-]+$/g, { message: 'Invalid character detected' }),
                    })
                    .when('idType', {
                        is: (val: keyof typeof CustomerIdTypeEnum) => {
                            return val === CustomerIdTypeEnum[CustomerIdTypeEnum.Passport];
                        },
                        then: (schema) => schema.max(12).label('Passport number').matchNumbersAndAlphabets(),
                    })
                    .when([], {
                        is: () => formik.values.additionalUserDetailsToggle,
                        then: (schema) => schema.required('Child ID Number is required'),
                        otherwise: (schema) => schema.notRequired().nullable(),
                    }),
                nationality: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().oneOf(countries.map(country => country.name), 'Invalid country').label('Nationality').required('Nationality is required')
                    .when([], {
                        is: () => formik.values.additionalUserDetailsToggle,
                        then: (schema) => schema.required('Child nationality is required'),
                        otherwise: (schema) => schema.notRequired().nullable(),
                    }),
                race: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().oneOf(Object.values(CustomerRaceEnum).filter(e => Number.isNaN(Number(e))) as string[]).label('Race').required('Race is required')
                    .when([], {
                        is: () => formik.values.additionalUserDetailsToggle,
                        then: (schema) => schema.required('Child race is required'),
                        otherwise: (schema) => schema.notRequired().nullable(),
                    }),
            })
        ),
        additionalUserDetailsToggle: Yup.boolean(),
    });

    const CustomerManagementCreateCustomerFormInitialValues: TCreateNewCustomerValidation = {
        name: '',
        email: '',
        CIFNo: '',
        idType: '',
        idNo: '',
        contactNo: '',
        branchName: '',
        branchCode: '',
        nationality: '',
        occupation: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        postCode: '',
        race: '',
        fundType: '',
        refCode: '',
        accountType: CustomerAccTypeEnum.Individual,
        additionalUserDetails: [{ name: '', idType: '', idNo: '', nationality: '', race: '' }],
        additionalUserDetailsToggle: false,
    };

    const formik = useFormik({
        initialValues: CustomerManagementCreateCustomerFormInitialValues,
        validationSchema: CustomerManagementCreateCustomerFormValidation,
        enableReinitialize: true,
        onSubmit: (values) => {
            const castValues = CustomerManagementCreateCustomerFormValidation.cast(values);
            const {
                idType,
                race,
                fundType,
                accountType,
                additionalUserDetails,
                additionalUserDetailsToggle,
                refCode,
                ...restOfValues
            } = castValues;

            const additionalUserDetailsMap: TAdditionalUserDetails[] = additionalUserDetails
                ? additionalUserDetails.map((item) => {
                    return {
                        idNo: item.idNo as string,
                        name: item.name as string,
                        nationality: item.nationality as string,
                        idType: CustomerIdTypeEnum[item.idType as unknown as keyof typeof CustomerIdTypeEnum] || CustomerIdTypeEnum.IC,
                        race: CustomerRaceEnum[item.race as unknown as keyof typeof CustomerRaceEnum] || CustomerRaceEnum.Malay,
                    };
                })
                : [];

            onSubmit({
                idType: CustomerIdTypeEnum[idType as keyof typeof CustomerIdTypeEnum] || CustomerIdTypeEnum.IC,
                race: CustomerRaceEnum[race as keyof typeof CustomerRaceEnum] || CustomerRaceEnum.Malay,
                fundType: CustomerAccFundTypeEnum[fundType as keyof typeof CustomerAccFundTypeEnum] || CustomerAccFundTypeEnum.Islamic,
                accountType,
                additionalUserDetails: additionalUserDetailsToggle ? additionalUserDetailsMap : [],
                refCode: refCode ? sanitizeReferralCode(refCode) : undefined,
                ...restOfValues,
            });
        },
    });

    useEffect(() => {
        const massagedResult = [
            countryList.find(item => item.name === 'Malaysia'),
            ...countryList,
        ].filter(item => item);

        setCountries(massagedResult as Country[]);
    }, []);

    useEffect(() => {
        // Note: Not batching this setFieldValue with branchName's onChange setFieldValue because of a race condition
        // the branchName validation will fire first before the update is done, rendering an error for the branchName field
        const newBranch = branchesList.find(branch => branch.branchName === formik.values.branchName);

        if (newBranch) {
            formik.setFieldValue('branchCode', newBranch?.branchCode);
        } else {
            formik.setFieldValue('branchCode', '');
        }
    }, [formik.values.branchName, branchesList]);

    const idTypeOptions = Object.values(CustomerIdTypeEnum).filter(e => Number.isNaN(Number(e))).map(key => {
        return {
            label: key as string,
            value: key as string,
        };
    });

    const branchOptions = branchesList.filter(branch => branch.status === BranchStatusEnum.Active).map(branch => {
        return {
            label: branch.branchName,
            value: branch.branchName,
        };
    });

    const nationalityOptions = Object.values(countries).map(country => {
        const countryName = country.name;
        return {
            label: countryName,
            value: countryName,
        };
    });

    const raceOptions = Object.values(CustomerRaceEnum).filter(e => Number.isNaN(Number(e))).map(key => {
        return {
            label: key as string,
            value: key as string,
        };
    });

    // conventional type is hidden for the time being
    const fundTypeOptions = Object.values(CustomerAccFundTypeEnum).filter(e => Number.isNaN(Number(e))).map(key => {
        return {
            label: key as string,
            value: key as string,
        };
    });

    // to hide conventional use this code instead
    // const fundTypeOptions = [{
    //     label: CustomerAccFundTypeEnum[CustomerAccFundTypeEnum.Islamic],
    //     value: CustomerAccFundTypeEnum[CustomerAccFundTypeEnum.Islamic],
    // }];

    const renderAdditionalUserDetails = () => {
        return (
            <div className={FormStyles['form-container']}>
                {formik.values.additionalUserDetails?.map((userDetails, index) => {
                    return (
                        <React.Fragment key={index}>
                            <LabelledInput
                                id={`additionalUserDetails[${index}].name`}
                                name={`additionalUserDetails[${index}].name`}
                                label='Child Name* :'
                                value={formik.values.additionalUserDetails?.[index].name || ''}
                                error={formik.touched.additionalUserDetails?.[index]?.name ? (formik.errors.additionalUserDetails?.[index] as FormikErrors<TCreateNewCustomerAdditionalUsersReduxParams>)?.name : undefined}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={FormStyles['span-2']}
                                disabled={inputIsDisabled}
                            />

                            <LabelledDropdown
                                id={`additionalUserDetails[${index}].idType`}
                                name={`additionalUserDetails[${index}].idType`}
                                label='ID Type* :'
                                options={idTypeOptions}
                                value={formik.values.additionalUserDetails?.[index].idType || ''}
                                error={formik.touched.additionalUserDetails?.[index].idType ? (formik.errors.additionalUserDetails?.[index] as FormikErrors<TCreateNewCustomerAdditionalUsersReduxParams>)?.idType : undefined}
                                onChange={(e) => formik.setFieldValue(`additionalUserDetails[${index}].idType`, e.target.value)}
                                onBlur={formik.handleBlur}
                                autoComplete='off'
                                disabled={inputIsDisabled}
                                noOptionsMessage='No results found'
                                optionSelected={!!CustomerIdTypeEnum[formik.values.idType as keyof typeof CustomerIdTypeEnum]}
                            />

                            <LabelledInput
                                id='idNo'
                                name={`additionalUserDetails[${index}].idNo`}
                                label='ID No.* :'
                                value={formik.values.additionalUserDetails?.[index].idNo || ''}
                                // @ts-expect-error formik.errors.additionalUserDetails is not a string
                                error={formik.touched.additionalUserDetails?.[index].idNo ? formik.errors.additionalUserDetails?.[index].idNo : undefined}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={inputIsDisabled}
                            />

                            <LabelledDropdown
                                id='nationality'
                                name={`additionalUserDetails[${index}].nationality`}
                                label='Nationality* :'
                                options={nationalityOptions}
                                value={formik.values.additionalUserDetails?.[index].nationality || ''}
                                // @ts-expect-error formik.errors.additionalUserDetails is not a string
                                error={formik.touched.additionalUserDetails?.[index].nationality ? formik.errors.additionalUserDetails?.[index].nationality : undefined}
                                onChange={(e) => formik.setFieldValue(`additionalUserDetails[${index}].nationality`, e.target.value)}
                                onBlur={formik.handleBlur}
                                autoComplete='off'
                                noOptionsMessage='No country found'
                                disabled={inputIsDisabled}
                                optionSelected={!!nationalityOptions.find(option => option.value === formik.values.nationality)}
                            />

                            <LabelledDropdown
                                id='race'
                                name={`additionalUserDetails[${index}].race`}
                                label='Race* :'
                                options={raceOptions}
                                value={formik.values.additionalUserDetails?.[index].race || ''}
                                // @ts-expect-error formik.errors.additionalUserDetails is not a string
                                error={formik.touched.additionalUserDetails?.[index].race ? formik.errors.additionalUserDetails?.[index].race : undefined}
                                onChange={(e) => formik.setFieldValue(`additionalUserDetails[${index}].race`, e.target.value)}
                                onBlur={formik.handleBlur}
                                autoComplete='off'
                                disabled={inputIsDisabled}
                                noOptionsMessage='No race found'
                                optionSelected={!!raceOptions.find(option => option.value === formik.values.race)}
                            />
                        </React.Fragment>
                    );
                })}
            </div>
        );
    };

    const handleIsJointAccountClick = () => {
        formik.setFieldValue('additionalUserDetailsToggle', !formik.values.additionalUserDetailsToggle);

        if (!formik.values.additionalUserDetailsToggle) {
            formik.setFieldValue('accountType', CustomerAccTypeEnum.Joint);
        } else {
            formik.setFieldValue('accountType', CustomerAccTypeEnum.Individual);
        }
    };

    return (
        <form
            onSubmit={formik.handleSubmit}
            className={FormStyles.container}
            onKeyDown={blockEnterKey}
        >
            <div className={FormStyles['form-container']}>
                <LabelledInput
                    id='name'
                    name='name'
                    label='Customer Name* :'
                    value={formik.values.name}
                    error={formik.touched.name ? formik.errors.name : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={FormStyles['span-2']}
                    disabled={inputIsDisabled}
                />

                <LabelledInput
                    id='email'
                    name='email'
                    label='Email :'
                    value={formik.values.email}
                    error={formik.touched.email ? formik.errors.email : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled}
                />

                <LabelledInput
                    id='CIFNo'
                    name='CIFNo'
                    label='CIF No.* :'
                    value={formik.values.CIFNo}
                    error={formik.touched.CIFNo ? formik.errors.CIFNo : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled}
                />

                <LabelledDropdown
                    id='idType'
                    name='idType'
                    label='ID Type* :'
                    options={idTypeOptions}
                    value={formik.values.idType || ''}
                    error={formik.touched.idType ? formik.errors.idType : undefined}
                    onChange={(e) => formik.setFieldValue('idType', e.target.value)}
                    onBlur={formik.handleBlur}
                    autoComplete='off'
                    disabled={inputIsDisabled}
                    noOptionsMessage='No results found'
                    optionSelected={!!CustomerIdTypeEnum[formik.values.idType as keyof typeof CustomerIdTypeEnum]}
                />

                <LabelledInput
                    id='idNo'
                    name='idNo'
                    label='ID No.* :'
                    value={formik.values.idNo}
                    error={formik.touched.idNo ? formik.errors.idNo : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled}
                />

                <LabelledInput
                    id='contactNo'
                    name='contactNo'
                    label='Contact No.* :'
                    value={formik.values.contactNo}
                    error={formik.touched.contactNo ? formik.errors.contactNo : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled}
                />

                <LabelledDropdown
                    id='branchName'
                    name='branchName'
                    label='Branch Name* :'
                    options={branchOptions}
                    value={formik.values.branchName || ''}
                    error={formik.touched.branchName ? formik.errors.branchName : undefined}
                    onChange={(e) => formik.setFieldValue('branchName', e.target.value)}
                    autoComplete='off'
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled || !allBranchAccess}
                    optionSelected={!!branchOptions.find(option => option.value === formik.values.branchName)}
                />

                <LabelledInput
                    id='branchCode'
                    name='branchCode'
                    label='Branch Code* :'
                    value={formik.values.branchCode || ''}
                    error={formik.touched.branchCode ? formik.errors.branchCode : undefined}
                    disabled={true}
                />

                <LabelledDropdown
                    id='nationality'
                    name='nationality'
                    label='Nationality* :'
                    options={nationalityOptions}
                    value={formik.values.nationality}
                    error={formik.touched.nationality ? formik.errors.nationality : undefined}
                    onChange={(e) => formik.setFieldValue('nationality', e.target.value)}
                    onBlur={formik.handleBlur}
                    autoComplete='off'
                    noOptionsMessage='No country found'
                    disabled={inputIsDisabled}
                    optionSelected={!!nationalityOptions.find(option => option.value === formik.values.nationality)}
                />

                <LabelledInput
                    id='occupation'
                    name='occupation'
                    label='Occupation* :'
                    value={formik.values.occupation}
                    error={formik.touched.occupation ? formik.errors.occupation : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled}
                />

                <LabelledDropdown
                    id='race'
                    name='race'
                    label='Race* :'
                    options={raceOptions}
                    value={formik.values.race || ''}
                    error={formik.touched.race ? formik.errors.race : undefined}
                    onChange={(e) => formik.setFieldValue('race', e.target.value)}
                    onBlur={formik.handleBlur}
                    autoComplete='off'
                    disabled={inputIsDisabled}
                    noOptionsMessage='No race found'
                    optionSelected={!!raceOptions.find(option => option.value === formik.values.race)}
                />

                <LabelledInput
                    id='addressLine1'
                    name='addressLine1'
                    label='Address Line 1* :'
                    value={formik.values.addressLine1}
                    error={formik.touched.addressLine1 ? formik.errors.addressLine1 : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={FormStyles['span-2']}
                    disabled={inputIsDisabled}
                />

                <LabelledInput
                    id='addressLine2'
                    name='addressLine2'
                    label='Address Line 2 :'
                    value={formik.values.addressLine2}
                    error={formik.touched.addressLine2 ? formik.errors.addressLine2 : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled}
                />

                <LabelledInput
                    id='city'
                    name='city'
                    label='City* :'
                    value={formik.values.city}
                    error={formik.touched.city ? formik.errors.city : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled}
                />

                <LabelledInput
                    id='state'
                    name='state'
                    label='State* :'
                    value={formik.values.state}
                    error={formik.touched.state ? formik.errors.state : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    autoComplete='off'
                    disabled={inputIsDisabled}
                />

                <LabelledInput
                    id='postCode'
                    name='postCode'
                    label='Postcode* :'
                    value={formik.values.postCode}
                    error={formik.touched.postCode ? formik.errors.postCode : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled}
                />

                <LabelledDropdown
                    id='fundType'
                    name='fundType'
                    label='Fund Type* :'
                    options={fundTypeOptions}
                    value={formik.values.fundType || ''}
                    error={formik.touched.fundType ? formik.errors.fundType : undefined}
                    onChange={(e) => formik.setFieldValue('fundType', e.target.value)}
                    onBlur={formik.handleBlur}
                    autoComplete='off'
                    disabled={inputIsDisabled}
                    noOptionsMessage='No fund type found'
                    optionSelected={!!FundTypeEnum[formik.values.fundType as keyof typeof FundTypeEnum]}
                />

                <LabelledInput
                    id='refCode'
                    name='refCode'
                    label='Referral Code :'
                    value={formik.values.refCode}
                    error={formik.touched.refCode ? formik.errors.refCode : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled}
                />

                <div className={`${FormStyles['joint-account-details-container']}  ${FormStyles['span-2']}`}>
                    <Checkbox
                        isReversed
                        label='Is Joint Account'
                        isChecked={formik.values.additionalUserDetailsToggle}
                        onChange={handleIsJointAccountClick}
                    />

                    {formik.values.additionalUserDetailsToggle && renderAdditionalUserDetails()}
                </div>

                <div className={`${FormStyles['button-container']} ${FormStyles['span-2']}`}>
                    <div className={FormStyles['button-subcontainer']}>
                        <ActionButton
                            label={submitButtonLabel}
                            isLoading={actionButtonIsLoading}
                            type='submit'
                            disabled={actionButtonIsLoading || actionButtonIsDisabled}
                            className={actionButtonClassName}
                        />

                        <Text
                            label={error}
                            isError
                        />
                    </div>
                </div>
            </div>
        </form>
    );
};

export default CustomerManagementCreateCustomerForm;
